import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { DRIVE_ERROR_MESSAGES, withDriveToken, shareFileWithLink } from '../files/drive';
import { PASTED_FOLDER_NAME, pasteImageErrorMessage, uploadToPastedFolder } from './pasteImage';

/**
 * Attach-a-file, for the Messages page composer (plan-drop3-extras.md, E2).
 *
 * Deliberately NOT the task Files upload (files/queries.ts insertFileRecord):
 * that writes a task_files row, and task_files insert needs can_edit_project
 * — owner or project member (20260806000200_files_access.sql:187-193). The
 * three channel rooms have no project_members rows (20270168000100), so a
 * normal TAP member would be refused there. Reusing it would also spill chat
 * attachments into a room's Files list, which has no Files tab to begin with.
 *
 * Same Drive helpers and the same "TAP Pasted Images" folder as a pasted
 * screenshot (pasteImage.ts) — one folder for everything a message carries,
 * not two. The message body gets a plain link with the file's name; there is
 * no task_files row and nothing else in the database changes.
 */

/** E3: one file per send, 25 MB cap. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** E3: wording consistent with pasteImageErrorMessage's house style. */
export const ATTACHMENT_TOO_LARGE_MESSAGE = 'That file is larger than 25 MB.';

export function attachmentSizeError(file: File): string | null {
  return file.size > MAX_ATTACHMENT_BYTES ? ATTACHMENT_TOO_LARGE_MESSAGE : null;
}

export interface UploadedAttachment {
  name: string;
  /** Drive's webViewLink: this is a link, never inlined as an <img>. */
  url: string;
}

/** Turns an upload failure into the same wording the paste path already uses. */
export function attachmentErrorMessage(cause: unknown): string {
  return pasteImageErrorMessage(cause);
}

export function useAttachFileUpload(): (file: File) => Promise<UploadedAttachment> {
  const { client, access } = useAuth();
  const userId = access.status === 'approved' ? access.profile.id : '';

  return useCallback(
    async (file: File) => {
      const sizeError = attachmentSizeError(file);
      if (sizeError !== null) {
        throw new Error(sizeError);
      }
      try {
        return await withDriveToken(client, async (token) => {
          // The same remembered folder as a pasted screenshot (Option A).
          const uploaded = await uploadToPastedFolder(token, userId, file);
          await shareFileWithLink(token, uploaded.id);
          const url = uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`;
          return { name: uploaded.name, url };
        });
      } catch (cause) {
        // Re-thrown already readable, matching pasteImage.ts's own contract,
        // except a size refusal (thrown above, before any Drive call) which
        // already carries the exact wording this file owns.
        const code = cause instanceof Error ? cause.message : '';
        if (code === ATTACHMENT_TOO_LARGE_MESSAGE) {
          throw cause;
        }
        throw new Error(attachmentErrorMessage(cause), { cause });
      }
    },
    [client],
  );
}

// Re-exported so callers of this file do not also need to import
// pasteImage.ts for the one thing they share.
export { DRIVE_ERROR_MESSAGES, PASTED_FOLDER_NAME };
